import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiAccessApi } from '@/api/apiAccess.api';
import { useAuth } from '@/context/AuthContext';
import { ViewType } from '@/types/domain';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Badge } from '@/components/ui/Badge';
import { useToasts } from '@/context/ToastContext';
import { ApiError } from '@/lib/errors';

/** Absolute base of this server's API (what the external Sheet/Apps Script calls). */
function apiBaseUrl(): string {
  const env = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '');
  return `${env || window.location.origin}/api/v1`;
}

/** The ready-to-paste Google Apps Script, with this server's URL + key filled in. */
function buildAppsScript(apiBase: string, apiKey: string): string {
  return `/**
 * BDS Ticket Hub → Google Sheets sync.
 * Pulls Tickets + Activity from the Ticket Hub export API into this spreadsheet.
 * Setup: Extensions → Apps Script, paste this, Save, then run installBdsTrigger() once.
 */
var API_BASE = '${apiBase}';
var API_KEY  = '${apiKey}';
var TICKETS_SHEET = 'Tickets';
var ACTIVITY_SHEET = 'Activity';

function syncBdsTicketHub() {
  syncCollection_('tickets', TICKETS_SHEET, 'ticket_id', 'cursor', 'bds_tickets_cursor');
  syncCollection_('activity', ACTIVITY_SHEET, 'event_id', 'after', 'bds_activity_cursor');
}

function syncCollection_(path, sheetName, idKey, cursorParam, cursorProp) {
  var props = PropertiesService.getDocumentProperties();
  var cursor = props.getProperty(cursorProp) || '';
  var ss = SpreadsheetApp.getActiveSpreadsheet();
  var sheet = ss.getSheetByName(sheetName) || ss.insertSheet(sheetName);
  var first = true;
  for (var guard = 0; guard < 1000; guard++) {
    var url = API_BASE + '/sheets/' + path + '?limit=500' +
      (cursor ? ('&' + cursorParam + '=' + encodeURIComponent(cursor)) : '') +
      (first && cursor ? '&overlap_seconds=120' : '');
    first = false;
    var resp = UrlFetchApp.fetch(url, { headers: { 'X-API-Key': API_KEY }, muteHttpExceptions: true });
    if (resp.getResponseCode() !== 200) {
      throw new Error(sheetName + ' sync failed: HTTP ' + resp.getResponseCode() + ' ' + resp.getContentText().slice(0, 300));
    }
    var data = JSON.parse(resp.getContentText());
    var rows = data.rows || [];
    if (rows.length) writeRows_(sheet, rows, idKey);
    if (data.next_cursor) { cursor = data.next_cursor; props.setProperty(cursorProp, cursor); }
    if (!data.has_more) break;
  }
}

function writeRows_(sheet, rows, idKey) {
  var headers;
  if (sheet.getLastRow() === 0) {
    headers = Object.keys(rows[0]);
    sheet.getRange(1, 1, 1, headers.length).setValues([headers]).setFontWeight('bold');
    sheet.setFrozenRows(1);
  } else {
    headers = sheet.getRange(1, 1, 1, sheet.getLastColumn()).getValues()[0];
  }
  var idCol = headers.indexOf(idKey) + 1;
  var existing = {};
  if (idCol > 0 && sheet.getLastRow() > 1) {
    var ids = sheet.getRange(2, idCol, sheet.getLastRow() - 1, 1).getValues();
    for (var i = 0; i < ids.length; i++) existing[ids[i][0]] = i + 2;
  }
  var appends = [];
  for (var r = 0; r < rows.length; r++) {
    var rowArr = headers.map(function (h) {
      var v = rows[r][h];
      return v === null || v === undefined ? '' : v;
    });
    var id = rows[r][idKey];
    if (existing[id]) {
      sheet.getRange(existing[id], 1, 1, rowArr.length).setValues([rowArr]);
    } else {
      appends.push(rowArr);
    }
  }
  if (appends.length) {
    sheet.getRange(sheet.getLastRow() + 1, 1, appends.length, headers.length).setValues(appends);
  }
}

/** Run ONCE: installs a 10-minute auto-sync trigger and does an initial pull. */
function installBdsTrigger() {
  ScriptApp.getProjectTriggers().forEach(function (t) {
    if (t.getHandlerFunction() === 'syncBdsTicketHub') ScriptApp.deleteTrigger(t);
  });
  ScriptApp.newTrigger('syncBdsTicketHub').timeBased().everyMinutes(10).create();
  syncBdsTicketHub();
}`;
}

export function ApiPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { push } = useToasts();
  const queryClient = useQueryClient();
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const base = apiBaseUrl();

  useEffect(() => {
    if (user && user.view_type !== ViewType.Admin) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const statusQuery = useQuery({
    queryKey: ['api-access', 'status'],
    queryFn: () => apiAccessApi.status(),
    enabled: user?.view_type === ViewType.Admin
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['api-access', 'status'] });
  const errMsg = (e: unknown) => (e instanceof ApiError ? e.problem?.detail ?? e.message : 'Error');
  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      push({ kind: 'success', title: `${label} copied` });
    } catch {
      push({ kind: 'error', title: 'Copy failed', message: 'Select and copy manually.' });
    }
  };

  const genMut = useMutation({
    mutationFn: () => apiAccessApi.generateKey(),
    onSuccess: (r) => {
      setRevealedKey(r.api_key);
      push({ kind: 'success', title: 'API key generated', message: 'Copy it now — it is shown only once.' });
      void refresh();
    },
    onError: (e: unknown) => push({ kind: 'error', title: 'Could not generate key', message: errMsg(e) })
  });
  const enableMut = useMutation({
    mutationFn: (on: boolean) => (on ? apiAccessApi.enable() : apiAccessApi.disable()),
    onSuccess: (_r, on) => { push({ kind: 'success', title: on ? 'API enabled' : 'API disabled' }); void refresh(); },
    onError: (e: unknown) => push({ kind: 'error', title: 'Failed', message: errMsg(e) })
  });

  if (statusQuery.isLoading) return <LoadingState label="Loading API settings..." large />;
  if (statusQuery.isError) return <ErrorState error={statusQuery.error} onRetry={() => statusQuery.refetch()} />;
  const s = statusQuery.data;
  if (!s) return null;

  const ticketsUrl = `${base}/sheets/tickets`;
  const activityUrl = `${base}/sheets/activity`;
  const script = buildAppsScript(base, revealedKey ?? 'PASTE_YOUR_API_KEY');

  return (
    <div>
      <div className="section-header"><h2>API</h2></div>
      <div className="detail-grid">
        <div className="flex flex-col gap-4">
          {/* Status + key */}
          <div className="card">
            <div className="card-header"><h3 className="card-title">Sheets export API</h3></div>
            <div className="card-body">
              <p style={{ marginTop: 0 }}>
                Status:{' '}
                {s.enabled ? <Badge tone="success">On</Badge> : <Badge tone="neutral">Off</Badge>}
                {s.has_key ? <span className="text-small text-muted"> · key {s.key_hint}</span> : null}
                {s.env_fallback_present ? <span className="text-small text-muted"> · env key present</span> : null}
              </p>
              {s.key_generated_at ? (
                <p className="text-small text-muted" style={{ marginTop: 0 }}>
                  Key generated {new Date(s.key_generated_at).toLocaleString()}
                  {s.updated_by_name ? ` by ${s.updated_by_name}` : ''}
                </p>
              ) : null}

              {revealedKey ? (
                <div style={{ padding: 12, borderRadius: 8, background: '#ecfdf5', border: '1px solid #a7f3d0', margin: '10px 0' }}>
                  <div className="text-small" style={{ color: '#065f46', fontWeight: 600 }}>
                    Your new API key — copy it now, it won&apos;t be shown again:
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <code style={{ fontSize: 13, wordBreak: 'break-all' }}>{revealedKey}</code>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => copy(revealedKey, 'API key')}>Copy</button>
                  </div>
                </div>
              ) : null}

              <div className="flex gap-2" style={{ marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <button type="button" className="btn btn-primary" disabled={genMut.isPending}
                  onClick={() => {
                    if (!s.has_key || window.confirm('Rotate the key? The current key will stop working immediately and the Sheet must be updated with the new one.')) {
                      genMut.mutate();
                    }
                  }}>
                  {genMut.isPending ? 'Generating…' : s.has_key ? 'Rotate API key' : 'Generate API key'}
                </button>
                {s.has_key ? (
                  <button type="button" className="btn btn-secondary" disabled={enableMut.isPending}
                    onClick={() => enableMut.mutate(!s.enabled)}>
                    {s.enabled ? 'Turn off' : 'Turn on'}
                  </button>
                ) : null}
                <span className="text-small text-muted">Admin only. The key is shown once; store it safely.</span>
              </div>
            </div>
          </div>

          {/* Endpoints */}
          <div className="card">
            <div className="card-header"><h3 className="card-title">Endpoints (send the key as <code>X-API-Key</code>)</h3></div>
            <div className="card-body">
              {[['Tickets', ticketsUrl], ['Activity', activityUrl]].map(([label, url]) => (
                <div key={url} style={{ marginBottom: 8 }}>
                  <div className="text-small text-muted">{label}</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <code style={{ fontSize: 12, wordBreak: 'break-all' }}>{url}</code>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => copy(url, `${label} URL`)}>Copy</button>
                  </div>
                </div>
              ))}
              <p className="text-small text-muted" style={{ marginBottom: 0 }}>
                Read-only. Returns JSON, keyset-paginated (pass back <code>next_cursor</code> as <code>cursor</code>/<code>after</code>).
                Exposes customer data — treat the key like a password.
              </p>
            </div>
          </div>

          {/* Apps Script */}
          <div className="card">
            <div className="card-header"><h3 className="card-title">Google Sheets setup (Apps Script)</h3></div>
            <div className="card-body">
              <p className="text-small text-muted" style={{ marginTop: 0 }}>
                Generate a key above, then paste this into your Sheet (Extensions → Apps Script) and run
                <code> installBdsTrigger()</code> once. It creates <strong>Tickets</strong> + <strong>Activity</strong> tabs and syncs every 10 minutes.
              </p>
              <div className="flex gap-2" style={{ marginBottom: 8 }}>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => copy(script, 'Apps Script')}>Copy script</button>
                {!revealedKey ? <span className="text-small text-muted">Tip: generate the key first so it&apos;s filled in for you.</span> : null}
              </div>
              <pre style={{ maxHeight: 260, overflow: 'auto', fontSize: 11.5 }}><code>{script}</code></pre>
            </div>
          </div>
        </div>

        <aside className="flex flex-col gap-4">
          <div className="card">
            <div className="card-header"><h3 className="card-title">How it works</h3></div>
            <div className="card-body text-small">
              <ol style={{ paddingLeft: 18, margin: 0 }}>
                <li>You (Admin) <strong>generate the API key</strong> here.</li>
                <li>Open your Google Sheet → Extensions → Apps Script.</li>
                <li>Paste the script, Save, run <strong>installBdsTrigger()</strong> once (authorize it).</li>
                <li>It backfills everything, then auto-syncs every 10 min into the <strong>Tickets</strong> + <strong>Activity</strong> tabs.</li>
                <li>Share the Sheet (not the key) with your team.</li>
              </ol>
              <p style={{ marginTop: 10, marginBottom: 0 }}>
                Also works with Make/Zapier/n8n — point them at the endpoints with the same <code>X-API-Key</code> header.
              </p>
            </div>
          </div>
          <div className="card">
            <div className="card-header"><h3 className="card-title">Security</h3></div>
            <div className="card-body text-small">
              The key is stored only as a hash (shown once). Rotating it instantly invalidates the old one — update the Sheet after.
              The endpoints are read-only and return customer data, so keep the key private and rotate it if it leaks.
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
